import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts';

const PIE_COLORS = ['#2563eb','#16a34a','#d97706','#7c3aed','#db2777','#0891b2','#65a30d','#dc2626'];

function fmtMins(m) {
  m = parseFloat(m) || 0;
  if (m < 60) return `${Math.round(m)}m`;
  return `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
}

function elapsed(startedAt) {
  const m = Math.floor((Date.now() - new Date(startedAt)) / 60000);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m/60)}h ${m%60}m`;
}

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2);
}

function ProgressBar({ value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const color = pct === 100 ? 'var(--green)' : pct > 50 ? 'var(--blue)' : 'var(--amber)';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--gray3)', marginBottom:3 }}>
        <span>{value} / {max} ops</span>
        <span style={{ fontWeight:600, color }}>{pct}%</span>
      </div>
      <div className="progress-bar-wrap">
        <div className="progress-bar" style={{ width:`${pct}%`, background:color }} />
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'10px 14px', fontSize:13, boxShadow:'0 2px 8px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>{d.name}</div>
      <div style={{ color:'#6b7280' }}>{fmtMins(d.value)} &nbsp;·&nbsp; {d.pct}%</div>
    </div>
  );
}

function renderLabel({ cx, cy, midAngle, innerRadius, outerRadius, pct, name }) {
  if (pct < 5) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>
      {pct}%
    </text>
  );
}

function JobPieChart({ job, opTimes }) {
  const [selectedAsm, setSelectedAsm] = useState(null);

  // Build assembly-level data
  const asmMap = {};
  opTimes.forEach(op => {
    if (!asmMap[op.assembly]) asmMap[op.assembly] = { name: op.assembly, value: 0, ops: [] };
    asmMap[op.assembly].value += parseFloat(op.total_minutes) || 0;
    asmMap[op.assembly].ops.push(op);
  });
  const asmData = Object.values(asmMap).filter(a => a.value > 0);
  const asmTotal = asmData.reduce((s, a) => s + a.value, 0);
  asmData.forEach(a => { a.pct = asmTotal > 0 ? Math.round((a.value / asmTotal) * 100) : 0; });

  // Build op-level data for selected assembly
  const selectedOps = selectedAsm ? (asmMap[selectedAsm]?.ops || []) : [];
  const opData = selectedOps
    .map(op => ({ name: op.operation, value: parseFloat(op.total_minutes) || 0, status: op.status }))
    .filter(o => o.value > 0);
  const opTotal = opData.reduce((s, o) => s + o.value, 0);
  opData.forEach(o => { o.pct = opTotal > 0 ? Math.round((o.value / opTotal) * 100) : 0; });

  const hasTime = asmData.length > 0;

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div>
          <span style={{ fontWeight:700, fontSize:15 }}>{job.job_number}</span>
          <span style={{ color:'var(--gray3)', fontSize:14, marginLeft:8 }}>{job.title}</span>
        </div>
        <span style={{ fontSize:13, color:'var(--gray3)' }}>{fmtMins(asmTotal)} total</span>
      </div>

      {!hasTime && (
        <div style={{ color:'var(--gray4)', fontSize:14, textAlign:'center', padding:'24px 0' }}>
          No time logged yet
        </div>
      )}

      {hasTime && (
        <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
          {/* Assembly pie */}
          <div style={{ flex:1, minWidth:260 }}>
            <div style={{ fontSize:12, fontWeight:600, color:'var(--gray3)', marginBottom:6, textTransform:'uppercase', letterSpacing:'.05em' }}>
              By Assembly {selectedAsm ? '— click a slice to drill in' : '— click a slice to see ops'}
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={asmData}
                  cx="50%" cy="50%"
                  outerRadius={90}
                  dataKey="value"
                  labelLine={false}
                  label={renderLabel}
                  onClick={(d) => setSelectedAsm(prev => prev === d.name ? null : d.name)}
                  style={{ cursor:'pointer' }}
                >
                  {asmData.map((entry, i) => (
                    <Cell
                      key={entry.name}
                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                      opacity={selectedAsm && selectedAsm !== entry.name ? 0.4 : 1}
                      stroke={selectedAsm === entry.name ? '#111' : 'none'}
                      strokeWidth={selectedAsm === entry.name ? 2 : 0}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(value, entry) => (
                    <span style={{ fontSize:12, color:'#374151' }}>
                      {value} <span style={{ color:'#9ca3af' }}>{fmtMins(entry.payload.value)}</span>
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Operations drill-down pie */}
          {selectedAsm && opData.length > 0 && (
            <div style={{ flex:1, minWidth:260 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ fontSize:12, fontWeight:600, color:'var(--gray3)', textTransform:'uppercase', letterSpacing:'.05em' }}>
                  {selectedAsm} — Operations
                </div>
                <button
                  onClick={() => setSelectedAsm(null)}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'var(--gray3)', lineHeight:1 }}
                >✕</button>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={opData}
                    cx="50%" cy="50%"
                    outerRadius={90}
                    dataKey="value"
                    labelLine={false}
                    label={renderLabel}
                  >
                    {opData.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    formatter={(value, entry) => (
                      <span style={{ fontSize:12, color:'#374151' }}>
                        {value} <span style={{ color:'#9ca3af' }}>{fmtMins(entry.payload.value)}</span>
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {selectedAsm && opData.length === 0 && (
            <div style={{ flex:1, minWidth:260, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gray4)', fontSize:14 }}>
              No time logged on operations in {selectedAsm}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function JobPieChartWithSelector({ jobs, op_times }) {
  const [selectedJobId, setSelectedJobId] = useState(jobs[0]?.id || '');
  const job = jobs.find(j => j.id === selectedJobId) || jobs[0];
  const jobOpTimes = op_times.filter(o => o.job_number === job?.job_number);

  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
        <div className="section-title" style={{ marginBottom:0 }}>Time breakdown by job</div>
        <select
          value={selectedJobId}
          onChange={e => setSelectedJobId(e.target.value)}
          style={{ padding:'5px 10px', border:'1.5px solid var(--gray5)', borderRadius:8, fontSize:13, background:'#fff', cursor:'pointer' }}
        >
          {jobs.map(j => (
            <option key={j.id} value={j.id}>{j.job_number} — {j.title}</option>
          ))}
        </select>
      </div>
      {job && <JobPieChart job={job} opTimes={jobOpTimes} />}
    </div>
  );
}

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:8, padding:'10px 14px', fontSize:13, boxShadow:'0 2px 8px rgba(0,0,0,.12)' }}>
      <div style={{ fontWeight:700, marginBottom:4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {parseFloat(p.value).toFixed(1)}h
        </div>
      ))}
    </div>
  );
}

function JobTrendChart({ jobTrends }) {
  if (!jobTrends || jobTrends.length === 0) return null;
  const hasEstimates = jobTrends.some(j => parseFloat(j.estimated_hours) > 0);

  const data = jobTrends.map(j => ({
    job: j.job_number,
    title: j.title,
    actual: parseFloat(j.total_hours) || 0,
    estimated: parseFloat(j.estimated_hours) || 0,
    status: j.status,
  }));

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <div className="section-title" style={{ marginBottom:0 }}>Hours per job</div>
        <div style={{ display:'flex', gap:16, fontSize:12, color:'var(--gray3)' }}>
          <span><span style={{ display:'inline-block', width:10, height:10, background:'#2563eb', borderRadius:2, marginRight:4 }}/>Actual</span>
          {hasEstimates && <span><span style={{ display:'inline-block', width:10, height:10, background:'#d1d5db', borderRadius:2, marginRight:4 }}/>Estimated</span>}
        </div>
      </div>
      <div style={{ fontSize:12, color:'var(--gray4)', marginBottom:12 }}>Oldest → newest · includes completed jobs</div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top:4, right:16, left:0, bottom:40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis
            dataKey="job"
            tick={{ fontSize:11, fill:'#6b7280' }}
            angle={-35}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            tick={{ fontSize:11, fill:'#6b7280' }}
            tickFormatter={v => `${v}h`}
            width={40}
          />
          <Tooltip content={<TrendTooltip />} />
          {hasEstimates && (
            <Bar dataKey="estimated" name="Estimated" fill="#d1d5db" radius={[3,3,0,0]} maxBarSize={32} />
          )}
          <Bar dataKey="actual" name="Actual" radius={[3,3,0,0]} maxBarSize={32}>
            {data.map((entry, i) => (
              <Cell
                key={entry.job}
                fill={entry.status === 'complete' ? '#16a34a' : entry.status === 'in_progress' ? '#2563eb' : '#9ca3af'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display:'flex', gap:16, fontSize:12, color:'var(--gray4)', justifyContent:'center', marginTop:4 }}>
        <span><span style={{ display:'inline-block', width:8, height:8, background:'#16a34a', borderRadius:'50%', marginRight:4 }}/>Complete</span>
        <span><span style={{ display:'inline-block', width:8, height:8, background:'#2563eb', borderRadius:'50%', marginRight:4 }}/>In progress</span>
        <span><span style={{ display:'inline-block', width:8, height:8, background:'#9ca3af', borderRadius:'50%', marginRight:4 }}/>Pending</span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/dashboard');
      setData(d);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (loading) return <div className="loading-screen">Loading dashboard…</div>;

  const { jobs = [], active_workers = [], op_times = [], job_trends = [] } = data || {};
  const totalHours = jobs.reduce((s, j) => s + parseFloat(j.total_hours || 0), 0);
  const inProgressJobs = jobs.filter(j => j.status === 'in_progress');

  return (
    <div className="page">
      <div className="topbar">
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src="/logo.jpg" alt="Logo" style={{ height:32, objectFit:'contain' }} />
          <h1>Dashboard</h1>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:12, color:'var(--gray3)' }}>
            Updated {lastRefresh.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
          </span>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/work')}>
            Worker view
          </button>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/floor')}>
            Floor display
          </button>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/terminal')}>
            Terminal
          </button>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/timesheet')}>
            Timesheets
          </button>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/users')}>
            Users
          </button>
          <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={logout}>
            Sign out
          </button>
        </div>
      </div>

      <div className="content">

        {/* ── Summary stats ── */}
        <div className="grid-2" style={{ marginBottom:16 }}>
          <div className="card stat-box">
            <div className="value">{inProgressJobs.length}</div>
            <div className="label">Jobs in progress</div>
          </div>
          <div className="card stat-box">
            <div className="value" style={{ color:'var(--green)' }}>{active_workers.length}</div>
            <div className="label">Workers clocked in</div>
          </div>
          <div className="card stat-box">
            <div className="value" style={{ color:'var(--amber)' }}>{totalHours.toFixed(1)}</div>
            <div className="label">Total hours logged</div>
          </div>
          <div className="card stat-box">
            <div className="value" style={{ color:'var(--gray2)' }}>{jobs.length}</div>
            <div className="label">Active jobs tracked</div>
          </div>
        </div>

        {/* ── Live workers ── */}
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title">
            Live — workers on the floor
            {active_workers.length === 0 && (
              <span style={{ fontWeight:400, color:'var(--gray4)', fontSize:13, marginLeft:8 }}>
                Nobody clocked in
              </span>
            )}
          </div>
          {active_workers.map(w => (
            <div key={w.id + w.started_at} className="worker-row">
              <div className="avatar">{initials(w.name)}</div>
              <div className="worker-info">
                <div className="wname">{w.name}</div>
                <div className="wop">{w.operation} · {w.assembly} · {w.job_number}</div>
              </div>
              <div className="elapsed">{elapsed(w.started_at)}</div>
            </div>
          ))}
        </div>

        {/* ── Jobs list ── */}
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div className="section-title" style={{ marginBottom:0 }}>Jobs</div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-ghost" style={{ padding:'6px 14px', fontSize:13 }} onClick={() => navigate('/jobs/deleted')}>
                Deleted jobs
              </button>
              <button className="btn btn-primary" style={{ padding:'6px 14px', fontSize:13 }} onClick={() => navigate('/jobs/new')}>
                + New job
              </button>
            </div>
          </div>
          {jobs.length === 0 && (
            <div style={{ color:'var(--gray3)', fontSize:14, padding:'12px 0' }}>
              No jobs yet. Create your first job to get started.
            </div>
          )}
          {jobs.map(j => (
            <div key={j.id} className="job-row" onClick={() => navigate(`/jobs/${j.id}`)}>
              <div className="jnum">{j.job_number}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="jtitle">{j.title}</div>
                <ProgressBar value={parseInt(j.complete_ops)||0} max={parseInt(j.total_ops)||0} />
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, marginLeft:10 }}>
                <span className={`badge badge-${j.status}`}>{j.status.replace('_',' ')}</span>
                <span style={{ fontSize:12, color:'var(--gray3)' }}>{parseFloat(j.total_hours||0).toFixed(1)}h</span>
              </div>
            </div>
          ))}
        </div>

        {/* ── Job trend bar chart ── */}
        <JobTrendChart jobTrends={job_trends} />

        {/* ── Job pie chart ── */}
        {inProgressJobs.length > 0 && (
          <JobPieChartWithSelector jobs={inProgressJobs} op_times={op_times} />
        )}

        {/* ── Operation time table ── */}
        {op_times.length > 0 && (
          <div className="card">
            <div className="section-title">Operation time breakdown</div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ color:'var(--gray3)', textAlign:'left', borderBottom:'1px solid var(--gray5)' }}>
                    <th style={{ padding:'6px 8px', fontWeight:600 }}>Job</th>
                    <th style={{ padding:'6px 8px', fontWeight:600 }}>Assembly</th>
                    <th style={{ padding:'6px 8px', fontWeight:600 }}>Operation</th>
                    <th style={{ padding:'6px 8px', fontWeight:600, textAlign:'right' }}>Actual</th>
                    <th style={{ padding:'6px 8px', fontWeight:600, textAlign:'right' }}>Est.</th>
                    <th style={{ padding:'6px 8px', fontWeight:600 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {op_times.map(o => {
                    const over = o.estimated_minutes && o.total_minutes > o.estimated_minutes;
                    return (
                      <tr key={o.id} style={{ borderBottom:'1px solid var(--gray5)' }}>
                        <td style={{ padding:'8px', color:'var(--gray3)' }}>{o.job_number}</td>
                        <td style={{ padding:'8px' }}>{o.assembly}</td>
                        <td style={{ padding:'8px', fontWeight:500 }}>{o.operation}</td>
                        <td style={{ padding:'8px', textAlign:'right', fontWeight:600, color: over ? 'var(--red)' : 'inherit' }}>
                          {o.total_minutes}m
                        </td>
                        <td style={{ padding:'8px', textAlign:'right', color:'var(--gray3)' }}>
                          {o.estimated_minutes ? `${o.estimated_minutes}m` : '—'}
                        </td>
                        <td style={{ padding:'8px' }}>
                          <span className={`badge badge-${o.status}`}>{o.status.replace('_',' ')}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
